
import { supabase } from "@/integrations/supabase/client";
import { Service } from "@/types/service";
import { ServiceWithCategory } from "@/types/serviceQueryTypes";
import { QUERY_VERSION, serviceQueryStrings } from "@/constants/serviceQueryConstants";
import { isProduction } from "@/utils/environment";
import {
  Service as SimplifiedService,
} from "@/types/simplifiedService";
import {
  logEntry,
  logExit,
  logQuery,
  addLog
} from '@/utils/enhancedLogging';

export const getServiceById = async (id: string | number): Promise<Service | null> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select(`
        *,
        service_images (
          id,
          is_index_image,
          is_details_image,
          display_order,
          bucket,
          original_path,
          checksum
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error("Error fetching service by ID:", error);
      return null;
    }

    return data as Service;
  } catch (error) {
    console.error("Error fetching service by ID:", error);
    return null;
  }
};

// For backward compatibility
export const fetchServiceById = getServiceById;

export const getServicesByCategory = async (categoryId: string): Promise<Service[] | null> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('category_id', categoryId);

    if (error) {
      console.error("Error fetching services by category:", error);
      return null;
    }

    return data as Service[];
  } catch (error) {
    console.error("Error fetching services by category:", error);
    return null;
  }
};

export const getAllServices = async (): Promise<Service[] | null> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('*');

    if (error) {
      console.error("Error fetching all services:", error);
      return null;
    }

    return data as Service[];
  } catch (error) {
    console.error("Error fetching all services:", error);
    return null;
  }
};

export const createService = async (service: Service): Promise<Service | null> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .insert([service])
      .select('*')
      .single();

    if (error) {
      console.error("Error creating service:", error);
      return null;
    }

    return data as Service;
  } catch (error) {
    console.error("Error creating service:", error);
    return null;
  }
};

export const updateService = async (id: string, updates: Partial<Service>): Promise<Service | null> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .update(updates)
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      console.error("Error updating service:", error);
      return null;
    }

    return data as Service;
  } catch (error) {
    console.error("Error updating service:", error);
    return null;
  }
};

export const deleteService = async (id: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', id);

    if (error) {
      console.error("Error deleting service:", error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting service:", error);
    return { success: false, error };
  }
};

export const fetchServices = async (
  searchQuery: string = '',
  publishedOnly: boolean = true,
  page: number = 1,
  pageSize: number = 10,
  categoryId: number | null = null,
  selectedRegion: string = 'nairobi'
) => {
  const entryId = logEntry('fetchServices', {
    searchQuery, publishedOnly, page, pageSize, categoryId, selectedRegion,
    queryVersion: QUERY_VERSION
  });

  try {
    // Calculate start and end for pagination
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const regionClause = selectedRegion ? `eq.${selectedRegion}` : 'not.is.null';

    logQuery('fetchServices:startQuery', {
      regionClause,
      start,
      end,
      queryVersion: QUERY_VERSION
    });

    // Build the query
    let query = supabase
      .from('services')
      .select(serviceQueryStrings[QUERY_VERSION], { count: 'exact' })
      .eq(publishedOnly ? 'published' : 'id', publishedOnly ? true : 'id')
      .eq('region', selectedRegion)
      .range(start, end);

    // Filter out test services in production
    if (isProduction()) {
      logQuery('fetchServices:filteringTestServices', { environment: 'production' });
      query = query.eq('test_only', false);
    }

    // Handle category/subcategory filtering
    if (categoryId !== null) {
      logQuery('fetchServices:addingCategoryFilter', { categoryId });
      
      // First check if this is a subcategory ID
      const { data: subcategory, error: subcatError } = await supabase
        .from('subcategories')
        .select('id, parent_category')
        .eq('id', categoryId)
        .single();

      if (subcatError && subcatError.code !== 'PGRST116') { // PGRST116 is "not found"
        throw subcatError;
      }

      if (subcategory) {
        // This is a subcategory ID, filter directly
        logQuery('fetchServices:filteringBySubcategory', { subcategoryId: categoryId });
        query = query.eq('subcategory', categoryId);
      } else {
        // This is a category ID, get all its subcategories
        logQuery('fetchServices:filteringByCategory', { categoryId });
        const { data: subcategories, error: subcatsError } = await supabase
          .from('subcategories')
          .select('id')
          .eq('parent_category', categoryId);

        if (subcatsError) {
          throw subcatsError;
        }

        if (subcategories && subcategories.length > 0) {
          // Filter services by these subcategory IDs
          const subcategoryIds = subcategories.map(sub => sub.id);
          query = query.in('subcategory', subcategoryIds);
        } else {
          // No subcategories found for this category, return no results
          return {
            services: [],
            count: 0,
            totalPages: 0,
            isUsingFallback: false
          };
        }
      }
    }

    // Add search filter if provided
    if (searchQuery && searchQuery.trim() !== '') {
      logQuery('fetchServices:addingSearchFilter', { searchQuery });
      query = query.textSearch('name', searchQuery, {
        type: 'websearch',
        config: 'english'
      });
    }

    // Order by featured first, then display_order, then created_at
    query = query
      .order('featured', { ascending: false, nullsLast: true })
      .order('display_order', { ascending: true, nullsLast: true })
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    // Handle API errors with detailed logging
    if (error) {
      // Code 94 (from serviceOperations.ts:94)
      addLog('error', 'Error fetching services', 'serviceOperations:94', error);

      logExit('fetchServices', entryId, {
        status: 'error',
        error
      });

      return {
        services: [],
        count: 0,
        totalPages: 0,
        error,
        isUsingFallback: false
      };
    }

    if (!data || data.length === 0) {
      logQuery('fetchServices:noData', { reason: 'No data returned from query' });
      logExit('fetchServices', entryId, { status: 'empty' });

      return {
        services: [],
        count: 0,
        totalPages: 0,
        isUsingFallback: false
      };
    }

    // Calculate total pages
    const totalPages = Math.ceil((count || 0) / pageSize);

    logQuery('fetchServices:success', {
      count: data.length,
      totalCount: count,
      totalPages
    });

    logExit('fetchServices', entryId, {
      status: 'success',
      count: data.length,
      totalCount: count,
      totalPages
    });

    return {
      services: data,
      count,
      totalPages,
      isUsingFallback: false
    };

  } catch (err: any) {
    // Code 131 (from serviceOperations.ts:131)
    addLog('error', 'Detailed error in fetchServices', 'serviceOperations:131', err);

    // Try fallback query if main query fails
    logQuery('fetchServices:tryingFallback', { error: err.message });

    try {
      // Fallback with simplified query structure
      const fallbackEntryId = logEntry('fetchServices:fallback', {
        searchQuery, publishedOnly, page, pageSize, categoryId, selectedRegion
      });

      logQuery('fetchServices:fallbackQuery', { queryVersion: 'fallback' });

      const start = (page - 1) * pageSize;
      const end = start + pageSize - 1;

      // Simplified query with minimal joins
      let query = supabase
        .from('services')
        .select('*', { count: 'exact' })
        .eq(publishedOnly ? 'published' : 'id', publishedOnly ? true : 'id')
        .eq('region', selectedRegion)
        .range(start, end);

      // Filter out test services in production (fallback too)
      if (isProduction()) {
        query = query.eq('test_only', false);
      }

      if (categoryId !== null) {
        // Check if this is a subcategory ID first
        const { data: subcategory, error: subcatError } = await supabase
          .from('subcategories')
          .select('id')
          .eq('id', categoryId)
          .single();

        if (subcatError && subcatError.code !== 'PGRST116') {
          throw subcatError;
        }

        if (subcategory) {
          // Direct subcategory filter
          query = query.eq('subcategory', categoryId);
        } else {
          // Get all subcategories for this category
          const { data: subcategories, error: subcatsError } = await supabase
            .from('subcategories')
            .select('id')
            .eq('parent_category', categoryId);

          if (subcatsError) {
            throw subcatsError;
          }

          if (subcategories && subcategories.length > 0) {
            query = query.in('subcategory', subcategories.map(sub => sub.id));
          } else {
            return {
              services: [],
              count: 0,
              totalPages: 0,
              isUsingFallback: true
            };
          }
        }
      }

      if (searchQuery && searchQuery.trim() !== '') {
        query = query.ilike('name', `%${searchQuery}%`);
      }

      query = query
        .order('featured', { ascending: false, nullsLast: true })
        .order('display_order', { ascending: true, nullsLast: true })
        .order('created_at', { ascending: false });

      const { data, count, error } = await query;

      logExit('fetchServices:fallback', fallbackEntryId, {
        status: error ? 'error' : 'success',
        count: data?.length,
        error
      });

      if (error) {
        logExit('fetchServices', entryId, {
          status: 'allFailed',
          mainError: err.message,
          fallbackError: error.message
        });

        return {
          services: [],
          count: 0,
          totalPages: 0,
          error,
          isUsingFallback: true
        };
      }

      const totalPages = Math.ceil((count || 0) / pageSize);

      logExit('fetchServices', entryId, {
        status: 'fallbackSuccess',
        count: data?.length,
        totalCount: count,
        totalPages
      });

      return {
        services: data || [],
        count: count || 0,
        totalPages,
        isUsingFallback: true
      };

    } catch (fallbackErr) {
      logQuery('fetchServices:fallbackFailed', { error: fallbackErr });

      logExit('fetchServices', entryId, {
        status: 'allFailed',
        error: err
      });

      throw err;
    }
  }
};
